import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatError,
} from '@angular/material/input';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';

export interface SearchableSelectOption {
	value: any;
	label: string;
}

@Component({
	selector: 'formly-searchable-select',
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './searchable-select.type.html',
	imports: [
		MatFormField,
		MatLabel,
		NgIf,
		MatInput,
		ReactiveFormsModule,
		FormlyModule,
		MatSelect,
		MatOption,
		MatSelectSearchComponent,
		NgFor,
		MatError,
		AsyncPipe,
	],
})
export class SearchableSelectType
	extends FieldType<FieldTypeConfig>
	implements OnInit, OnDestroy
{
	private parentSub = new Subscription();
	protected filterCtrl = new FormControl('');
	protected filteredOptions$ = new ReplaySubject<SearchableSelectOption[]>(1);
	private _onDestroy = new Subject<void>();
	protected isOptionExist: boolean = true;

	public ngOnInit(): void {
		// Initialize options
		this.loadOptions();

		// Setup filter subscription
		this.parentSub.add(
			this.filterCtrl.valueChanges
				.pipe(takeUntil(this._onDestroy))
				.subscribe(() => {
					this.filterOptions();
				})
		);

		if (this.props.readonly) {
			const options = this.props.options;
			if (options instanceof Observable) {
				// Observable of options
				this.parentSub.add(
					options
						.pipe(takeUntil(this._onDestroy))
						.subscribe((data) =>
							this.formControl.setValue(
								this.formatOptions(data).find(
									(option) => option.value === this.formControl.value
								)?.label
							)
						)
				);
			}
		}
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	protected onSelectionChange(event: any): void {
		// Call the change callback if provided
		if (this.props.change) {
			this.props.change(this.field, event);
		}
	}

	protected addNew(name: string): void {
		// Your logic to add the new item to your data source
		const newItem = name;
		const options = this.props.options;
		// Handle different option formats
		if (Array.isArray(options)) {
			// Direct array of options
			options.push(newItem);
			// Optionally select the new item immediately
			this.formControl.setValue(newItem);
		} else if (options instanceof Observable) {
			// Observable of options
			this.parentSub.add(
				options.pipe(takeUntil(this._onDestroy)).subscribe((data) => {
					data.push(newItem);
					// Optionally select the new item immediately
					this.formControl.setValue(newItem);
				})
			);
		}
	}

	private loadOptions(): void {
		const options = this.props.options || [];
		// Handle different option formats
		if (Array.isArray(options)) {
			// Direct array of options
			if (
				this.formControl.value &&
				!options.some(
					(option) =>
						this.formControl.value === option[this.props.labelProp || 'label']
				)
			) {
				options.push({
					[this.props.labelProp || 'label']: this.formControl.value,
					[this.props.valueProp || 'value']: this.formControl.value,
				});
			}
			const formattedOptions = this.formatOptions(options);
			this.filteredOptions$.next(formattedOptions);
		} else if (options instanceof Observable) {
			// Observable of options
			this.parentSub.add(
				options.pipe(takeUntil(this._onDestroy)).subscribe((data) => {
					if (
						this.formControl.value &&
						!data.some(
							(option) =>
								this.formControl.value ===
								option[this.props.labelProp || 'label']
						)
					) {
						data.push({
							[this.props.labelProp || 'label']: this.formControl.value,
							[this.props.valueProp || 'value']: this.formControl.value,
						});
					}
					const formattedOptions = this.formatOptions(data);
					this.filteredOptions$.next(formattedOptions);
				})
			);
		}
		this.checkOptionExist();
	}

	private checkOptionExist(): void {
		const options = this.props.options || [];
		// Handle different option formats
		if (Array.isArray(options)) {
			// Direct array of options
			if (
				options.some(
					(option) =>
						this.filterCtrl.value?.toLowerCase() ===
						option[this.props.labelProp || 'label'].toLowerCase()
				)
			) {
				this.isOptionExist = true;
			} else {
				this.isOptionExist = false;
			}
		} else if (options instanceof Observable) {
			// Observable of options
			this.parentSub.add(
				options.pipe(takeUntil(this._onDestroy)).subscribe((data) => {
					if (
						data.some(
							(option) =>
								this.filterCtrl.value?.toLowerCase() ===
								option[this.props.labelProp || 'label'].toLowerCase()
						)
					) {
						this.isOptionExist = true;
					} else {
						this.isOptionExist = false;
					}
				})
			);
		}
	}

	private formatOptions(data: any[]): SearchableSelectOption[] {
		if (!data || !Array.isArray(data)) {
			return [];
		}

		const valueProp = this.props.valueProp || 'value';
		const labelProp = this.props.labelProp || 'label';

		return data.map((item) => ({
			value: typeof item === 'object' ? item[valueProp] : item,
			label: typeof item === 'object' ? item[labelProp] : item,
		}));
	}

	private filterOptions(): void {
		const options = this.props.options;

		if (!options) {
			return;
		}

		const search = this.filterCtrl.value?.toLowerCase() || '';

		if (Array.isArray(options)) {
			const formattedOptions = this.formatOptions(options);
			this.filteredOptions$.next(this.applyFilter(formattedOptions, search));
		} else if (options instanceof Observable) {
			this.parentSub.add(
				options.pipe(takeUntil(this._onDestroy)).subscribe((data) => {
					const formattedOptions = this.formatOptions(data);
					this.filteredOptions$.next(
						this.applyFilter(formattedOptions, search)
					);
				})
			);
		}
	}

	private applyFilter(
		options: SearchableSelectOption[],
		search: string
	): SearchableSelectOption[] {
		if (!search) {
			return options;
		}
		this.checkOptionExist();

		return options.filter((option) =>
			option.label.toLowerCase().includes(search)
		);
	}
}
